import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { AutoVideo } from "@/components/AutoVideo";
import { SEO } from "@/components/SEO";

const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const cleanHeroCopy = (s: string) =>
  (s || "")
    .replace(/\r\n/g, "\n")
    .replace(/\n+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const Index = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const reduce = useReducedMotion();
  const [activePortal, setActivePortal] = useState<number>(0);
  const [transitioning, setTransitioning] = useState<string | null>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const heroRef = useRef<HTMLDivElement>(null);
  const portalSectionRef = useRef<HTMLDivElement>(null);
  const isTouch = useMemo(() => isTouchDevice(), []);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const { scrollYProgress: portalProgress } = useScroll({
    target: portalSectionRef,
    offset: ["start end", "end start"],
  });

  const heroTitleY = useTransform(heroProgress, [0, 0.5], [0, reduce || isTouch ? 0 : -100]);

  const headerOpacity = useTransform(portalProgress, [0, 0.08, 0.15], [0, 1, 1]);
  const headerY = useTransform(portalProgress, [0, 0.1], reduce ? [0, 0] : [40, 0]);
  const portalContainerOpacity = useTransform(portalProgress, [0.05, 0.15], [0, 1]);

  useEffect(() => {
    const unsubscribe = portalProgress.on("change", (v) => {
      if (v < 0.33) setActivePortal(0);
      else if (v < 0.66) setActivePortal(1);
      else setActivePortal(2);
    });
    return unsubscribe;
  }, [portalProgress]);

  const directions = [
    {
      id: "create",
      title: "Create",
      tagline: "AI-powered image generation",
      route: "/generate",
      video: "/videos/SocialDirection-opt.mp4",
    },
    {
      id: "review",
      title: "Review",
      tagline: "Project collaboration & feedback",
      route: "/gallery",
      video: "/videos/cinematiccreator-opt.mp4",
    },
    {
      id: "generate",
      title: "Generate",
      tagline: "Your cinematic memory vault",
      route: "/generate",
      video: "/videos/Fullcinematicsuite-opt.mp4",
    },
  ];

  const heroSubtitle = useMemo(() => cleanHeroCopy(t("home.hero.subtitle") || ""), [t]);

  useEffect(() => {
    if (isTouch) return;
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (video) {
        if (key === directions[activePortal]?.id) video.play().catch(() => {});
        else video.pause();
      }
    });
  }, [activePortal, isTouch]);

  const handlePortalNavigate = useCallback(
    (direction: (typeof directions)[0]) => {
      setTransitioning(direction.id);
      setTimeout(() => navigate(direction.route), 500);
    },
    [navigate, directions],
  );

  const handlePortalHover = useCallback(
    (index: number) => {
      if (isTouch) return;
      setActivePortal(index);
      Object.entries(videoRefs.current).forEach(([key, video]) => {
        if (video) {
          if (key === directions[index].id) video.play().catch(() => {});
          else video.pause();
        }
      });
    },
    [isTouch, directions],
  );

  // Mouse parallax for hero
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });

  const handleHeroMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isTouch) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setMouseOffset({ x: x * 18, y: y * 12 });
  }, [isTouch]);

  return (
    <div className="bg-black text-white font-sans">
      <SEO
        title="Alabama Estudio — Consultoría IA y Producción"
        description="Impulsamos tu negocio con IA estratégica y contenido que conecta. Consultoría de IA, automatización, producción de contenido y storytelling."
        path="/"
      />
      <Navbar />

      {/* HERO — Full-bleed, NO vignette */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={() => setMouseOffset({ x: 0, y: 0 })}
        className="relative w-screen h-[100svh] overflow-hidden"
      >
        {/* Full-bleed video layer with mouse parallax */}
        <motion.div
          className="absolute inset-0"
          style={{
            transform: `translate3d(${mouseOffset.x * 0.5}px, ${mouseOffset.y * 0.5}px, 0) scale(1.08)`,
            transition: "transform 0.4s ease-out",
          }}
        >
          <AutoVideo src="/videos/hero-website-opt.mp4" preload="auto" />
        </motion.div>


        {/* Hero text — white, mix-blend-mode:difference for dynamic contrast */}
        <div className="absolute inset-0 flex items-center z-10">
          <div className="w-full px-6 md:px-12 lg:px-20">
            {isTouch ? (
              <div className="max-w-[75vw] pt-16" style={{ mixBlendMode: "difference", filter: "drop-shadow(0 1px 8px rgba(0,0,0,0.25))" }}>
                <h1 className="text-[11vw] sm:text-5xl font-serif font-light tracking-tight text-white leading-[0.92] mb-3 break-words">
                  {t("home.hero.titleLine1")}
                  <br />
                  {t("home.hero.titleLine2")}
                </h1>
                <p className="text-sm text-white/60 font-light leading-snug max-w-xs mt-4">{heroSubtitle}</p>
              </div>
            ) : (
              <motion.div
                className="max-w-[45vw]"
                style={{
                  y: heroTitleY,
                  mixBlendMode: "difference",
                  filter: "drop-shadow(0 1px 8px rgba(0,0,0,0.25))",
                  transform: `translate3d(${-mouseOffset.x * 0.3}px, ${-mouseOffset.y * 0.2}px, 0)`,
                  transition: "transform 0.35s ease-out",
                }}
              >
                <h1 className="text-6xl md:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] font-serif font-light tracking-tight text-white leading-[0.90] mb-4 break-words">
                  {t("home.hero.titleLine1")}
                  <br />
                  {t("home.hero.titleLine2")}
                </h1>
                <p className="text-base md:text-lg text-white/50 font-light leading-snug max-w-md mt-6">{heroSubtitle}</p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        {!isTouch && !reduce && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          >
            <motion.div
              className="w-5 h-8 rounded-full border border-white/20 flex justify-center pt-1.5"
              animate={{ y: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            >
              <div className="w-0.5 h-1.5 bg-white/30 rounded-full" />
            </motion.div>
          </motion.div>
        )}
      </section>

      {/* ─────────────────────────────────────────────────────────────
          FÚTBOL FEATURE — Phase 14.5
          Premium editorial doorway from the homepage into the canonical
          /world-cup-content surface.  Positioned immediately after the
          hero so it is the first cinematic campaign moment visitors
          meet — a doorway, not a near-footer promo.
          Re-uses the WCC hero footage so visitors who click feel
          they're entering the same world.  Native to the existing
          cinematic homepage typography + rhythm; deliberately NOT a
          banner or SaaS card.  Mexican Spanish, no torneo, no claimed
          clients, no World Cup affiliation.
          ───────────────────────────────────────────────────────────── */}
      <FutbolFeature />

      {/* DIRECTION PANELS */}
      {isTouch ? (
        <section className="relative bg-black" style={{ scrollSnapType: "y mandatory" }}>
          <div className="sticky top-0 z-20 px-6 pt-20 pb-8 bg-gradient-to-b from-black via-black/90 to-transparent pointer-events-none">
            <h2 className="text-3xl font-serif font-light tracking-tight text-white leading-[0.95] mb-2">
              Your creative world
            </h2>
            <p className="text-sm text-white/40 font-light">
              Three ways in.
            </p>
          </div>

          <div className="flex flex-col">
            {directions.map((direction, index) => (
              <div
                key={direction.id}
                onClick={() => navigate(direction.route)}
                className="relative min-h-[85svh] w-full overflow-hidden cursor-pointer active:opacity-90 transition-opacity"
                style={{ scrollSnapAlign: "start" }}
              >
                <AutoVideo src={direction.video} preload="metadata" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-6 pb-12">
                  <span className="block text-xs font-serif tracking-[0.4em] text-white/40 mb-3">0{index + 1}</span>
                  <h3 className="text-3xl font-serif font-light text-white mb-2">{direction.title}</h3>
                  <p className="text-sm text-white/50 font-light line-clamp-1 max-w-xs">{direction.tagline}</p>
                </div>

                {index < directions.length - 1 && <div className="absolute bottom-0 left-6 right-6 h-px bg-white/10" />}
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section ref={portalSectionRef} className="relative bg-black min-h-[100svh]">
          <div className="h-[100svh] overflow-hidden">
            <AnimatePresence>
              {transitioning && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 z-50 bg-black"
                  transition={{ duration: 0.5 }}
                />
              )}
            </AnimatePresence>

            <motion.div
              className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 lg:px-20 pt-28 md:pt-36"
              style={{ opacity: headerOpacity, y: headerY }}
            >
              <h2 className="text-6xl md:text-7xl lg:text-8xl font-serif font-light tracking-tight text-white leading-[0.95] mb-4">
                Your creative world
              </h2>
              <p className="text-lg text-white/30 font-light">
                Three ways in.
              </p>
            </motion.div>

            <motion.div className="absolute inset-0" style={{ opacity: portalContainerOpacity }}>
              <div className="w-full h-full flex flex-row">
                {directions.map((direction, index) => {
                  const isActive = activePortal === index;

                  return (
                    <motion.div
                      key={direction.id}
                      className="relative overflow-hidden cursor-pointer"
                      onClick={() => handlePortalNavigate(direction)}
                      onMouseEnter={() => handlePortalHover(index)}
                      animate={{ flex: isActive ? 1.25 : 0.875 }}
                      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <motion.div
                        className="absolute inset-0"
                        animate={{ scale: isActive ? 1 : 1.03 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <video
                          ref={(el) => { videoRefs.current[direction.id] = el; }}
                          autoPlay={index === 0}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        >
                          <source src={direction.video} type="video/mp4" />
                        </video>
                      </motion.div>

                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-black/25 to-black/35" />

                      <motion.div
                        className="absolute inset-0 pointer-events-none"
                        animate={{ opacity: isActive ? 0 : 1 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        style={{ background: "rgba(0,0,0,0.38)" }}
                      />

                      {index < directions.length - 1 && (
                        <div className="absolute top-0 right-0 w-px h-full bg-white/5 pointer-events-none" />
                      )}

                      <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 lg:p-14 pr-28">
                        <motion.h3
                          className="text-3xl md:text-4xl font-serif font-light tracking-tight text-white leading-tight mb-3"
                          animate={{ opacity: isActive ? 1 : 0.55, y: isActive ? 0 : 8 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {direction.title}
                        </motion.h3>

                        <motion.p
                          className="text-sm font-light text-white/40 max-w-sm line-clamp-1"
                          animate={{ opacity: isActive ? 0.75 : 0.25, y: isActive ? 0 : 14 }}
                          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {direction.tagline}
                        </motion.p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* No pagination dots */}
          </div>
        </section>
      )}


      {/* ACCOUNT INCENTIVE — cinematic editorial */}
      <section className="relative bg-black py-28 md:py-40 px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-80px" }}
          className="max-w-3xl"
        >
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-white mb-5">
            {language === "es" ? "Crea tu estudio." : "Create your studio."}
          </h2>
          <p className="font-sans text-base md:text-lg text-white/40 font-light leading-relaxed mb-12 max-w-lg">
            {language === "es"
              ? "Tu perfil, tu agente y tus generaciones — guardado."
              : "Your profile, your agent, your generations — saved."}
          </p>
          <button
            onClick={() => navigate("/playback/signup")}
            className="font-serif text-[10px] tracking-[0.3em] uppercase px-8 py-3 text-white/60 hover:text-white border border-white/15 hover:border-white/30 rounded-sm transition-all duration-500 bg-transparent"
          >
            {language === "es" ? "Crear cuenta" : "Create account"}
          </button>
        </motion.div>
      </section>
    </div>
  );
};

// -----------------------------------------------------------------------------
// FutbolFeature — Phase 14.5 homepage entry point into /world-cup-content.
//
// Intentionally a small standalone component so the homepage's existing
// composition stays readable.  Visual contract:
//   - full-bleed dark editorial moment, native to the cinematic homepage
//   - re-uses the WCC hero video (/wcc/hero.webm) for visual continuity
//   - large serif headline + tight Mexican-Spanish lede
//   - tasteful editorial CTA tag — not a button-style banner
//   - subtle scroll-linked parallax on the typography + video
//   - prefers-reduced-motion: static fallback (no transforms, just fade-in)
//   - clicking anywhere on the framed area navigates to /world-cup-content
//
// Copy is honest: opening requests, no claim of clients/partnerships/
// official affiliation, no "torneo".
// -----------------------------------------------------------------------------
function FutbolFeature() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement | null>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  // Slow editorial parallax — typography rises gently, video pushes down.
  const headlineY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const videoY = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.12]);

  return (
    <section
      ref={sectionRef}
      aria-label="Fútbol — temporada"
      className="relative bg-black overflow-hidden"
    >
      {/* Framed full-bleed editorial canvas.  Tall on desktop so the
          typography breathes; collapses gracefully on mobile. */}
      <button
        type="button"
        onClick={() => navigate("/world-cup-content")}
        className="group relative block w-full text-left min-h-[88svh] md:min-h-[96svh] focus:outline-none"
      >
        {/* Hero footage layer */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-0"
          style={
            reduce
              ? { transform: "scale(1.06)" }
              : { y: videoY, scale: videoScale, willChange: "transform" }
          }
        >
          <video
            className="w-full h-full object-cover"
            src="/wcc/hero.webm"
            poster="/wcc/hero-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
          />
        </motion.div>

        {/* Cinematic warm-to-black gradient + soft top/bottom vignette
            so the typography always sits in the deepest part of the
            frame regardless of which video frame is playing. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/85"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent"
        />

        {/* Editorial copy */}
        <motion.div
          className="absolute inset-0 z-10 flex items-end"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20 pb-16 md:pb-24">
            <motion.div
              style={reduce ? undefined : { y: headlineY, willChange: "transform" }}
            >
              <div className="font-serif text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-white/55 mb-5 md:mb-7 flex items-center gap-3">
                <span className="block w-1.5 h-1.5 rounded-full bg-white/70" />
                Temporada de fútbol · CDMX 2026 · Abrimos solicitudes
              </div>
              <h2 className="font-serif font-light tracking-tight leading-[0.92] text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7.5rem] max-w-[18ch]">
                Contenido para los <em className="italic">días de partido</em>.
              </h2>
              <p className="mt-6 md:mt-8 max-w-xl font-sans text-base md:text-lg text-white/65 font-light leading-relaxed">
                Estamos abriendo solicitudes especiales para restaurantes,
                bares, hoteles, rooftops, eventos y marcas locales que
                quieran contenido rápido, atractivo y listo para publicar
                durante la temporada de fútbol.
              </p>
              <div className="mt-10 md:mt-14 flex items-center gap-5">
                <span className="font-serif text-[11px] tracking-[0.32em] uppercase text-white/85 group-hover:text-white transition-colors duration-500">
                  Ver la propuesta
                </span>
                <span
                  aria-hidden="true"
                  className="block h-px w-12 md:w-20 bg-white/45 group-hover:w-24 md:group-hover:w-32 group-hover:bg-white/85 transition-all duration-500"
                />
                <span
                  aria-hidden="true"
                  className="font-serif text-lg text-white/85 group-hover:translate-x-1.5 transition-transform duration-500"
                >
                  →
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Top-right route hint — keeps the button area honest as a
            link without adding a hard pill. */}
        <span
          aria-hidden="true"
          className="absolute top-6 right-6 md:top-8 md:right-12 lg:right-20 font-serif text-[10px] tracking-[0.32em] uppercase text-white/45 group-hover:text-white/75 transition-colors duration-500"
        >
          /world-cup-content
        </span>
      </button>
    </section>
  );
}

export default Index;
